import { useWindowDimensions } from "@luce/core";

import { cn, isDev, platformProps } from "../../lib";
import { getIconSize } from "../../tokens";
import { typographyColorClass } from "../typography/typography.variants";
import { IconList } from "./icon-registry";
import type { IconProps } from "./types";

export const Icon = ({
  name,
  size,
  // Phosphor's own default too, pinned here so a library change can't move it
  weight = "regular",
  color,
  className,
  accessibilityLabel,
  testID,
}: IconProps) => {
  const IconComponent = IconList[name];
  const { isDesktop } = useWindowDimensions();
  const iconSize = typeof size === "number" ? size : getIconSize(size ?? "md", isDesktop);

  if (!IconComponent) {
    // Not Metro's `__DEV__`, which Vite doesn't define: it threw instead of warning
    if (isDev()) {
      console.warn(`[Icon] Unknown icon name: "${name}"`);
    }
    return null;
  }

  return (
    <IconComponent
      size={iconSize}
      weight={weight}
      // A token paints through its text class and `currentColor`. Without one,
      // Phosphor's default applies (the text colour)
      {...(color && { color: "currentColor" })}
      className={color ? cn(typographyColorClass[color], className) : className}
      // Decorative unless labelled: its internal name ("CaretDown") isn't a label
      {...(accessibilityLabel
        ? { role: "img", "aria-label": accessibilityLabel }
        : { "aria-hidden": true })}
      {...platformProps({ testID, slot: "icon" })}
    />
  );
};

Icon.displayName = "Icon";
